import { ref, readonly } from 'vue'

/**
 * Studio mode — swap real client identities for invented ones so the app can
 * be filmed or demoed without a real portfolio on screen.
 *
 * Two properties matter and the old implementation had neither:
 *
 *  1. SHAPE. A fake name keeps the real one's legal suffix, word count and
 *     nationality, because the prose around it does not change: a report that
 *     says "the German manufacturer" must not sit next to "Sunshine Corp".
 *     So the suffix is carried over verbatim and only the distinctive stem is
 *     replaced, from a pool matched to that suffix's region. A name with no
 *     suffix of its own ("BMW") is given its region's, because an invented
 *     word alone reads as a codename rather than a company.
 *
 *  2. STABILITY. Names are assigned by hashing the real name, not by a render
 *     counter. The same client is the same invented company on every page, in
 *     every session, in every take — reload mid-shoot and the footage still
 *     cuts together. The old counter made the mapping depend on which
 *     component happened to render first.
 *
 * The stems are invented, not borrowed: every one below returned zero hits on
 * the GLEIF LEI register (~2.7m legal entities worldwide — the universe a
 * credit portfolio draws from). See scripts/check-studio-names.mjs to re-run
 * that check when adding names. Zero LEI hits is not proof that no company
 * anywhere carries the name, but it clears the bar that matters here.
 */

// ── Invented stems, grouped by the region their ending suggests ─────
// Each has been checked against GLEIF and returned no registered entity.
const STEMS = {
  en: [
    'Aldenwick', 'Aldenmere', 'Aldencombe', 'Aldengate', 'Aldenstone',
    'Asterwick', 'Astermere', 'Astercombe', 'Astergate', 'Asterstone',
    'Bromwick', 'Brommere', 'Bromcombe', 'Bromgate', 'Caldenwick',
    'Caldenmere', 'Caldencombe', 'Caldengate', 'Caldenstone', 'Draywick',
    'Draymere', 'Draycombe', 'Draygate', 'Draystone', 'Elmwick', 'Elmmere',
    'Elmcombe', 'Esterwick', 'Estermere', 'Estercombe', 'Estergate',
    'Esterstone', 'Fennwick', 'Fennmere', 'Fenncombe', 'Fenngate',
    'Fennstone', 'Havenwick', 'Havenmere', 'Havencombe', 'Havengate',
    'Nylewick', 'Nylemere', 'Nylecombe', 'Nylegate', 'Nylestone', 'Rouswick',
    'Rousmere', 'Rouscombe', 'Rousgate', 'Rousstone', 'Steinwick',
    'Steinmere', 'Steincombe', 'Steingate', 'Steinstone', 'Vanewick',
    'Vanemere', 'Vanecombe', 'Vanegate', 'Vanestone', 'Ravwick', 'Ravmere',
    'Ravcombe', 'Ravgate', 'Ravstone'],
  de: [
    'Rheinhardt', 'Aldenhardt', 'Aldenfeld', 'Aldenwald', 'Aldenheim',
    'Asterhardt', 'Asterberg', 'Asterfeld', 'Asterwald', 'Asterheim',
    'Bromhardt', 'Bromfeld', 'Bromwald', 'Bromheim', 'Caldenhardt',
    'Caldenberg', 'Caldenfeld', 'Caldenwald', 'Caldenheim', 'Drayhardt',
    'Drayberg', 'Drayfeld', 'Draywald', 'Drayheim', 'Elmhardt', 'Elmfeld',
    'Elmwald', 'Elmheim', 'Esterhardt', 'Esterberg', 'Esterfeld',
    'Esterwald', 'Esterheim', 'Fennhardt', 'Fennberg', 'Fennfeld',
    'Fennwald', 'Fennheim', 'Havenhardt', 'Havenberg', 'Havenfeld',
    'Havenwald', 'Havenheim', 'Nylehardt', 'Nyleberg', 'Nylefeld',
    'Nylewald', 'Nyleheim', 'Roushardt', 'Rousberg', 'Rousfeld', 'Rouswald',
    'Rousheim', 'Vanehardt', 'Vaneberg', 'Vanefeld', 'Vanewald', 'Vaneheim',
    'Ravhardt', 'Ravberg', 'Ravfeld', 'Ravwald', 'Ravheim'],
  fr: [
    'Aldenmont', 'Aldencourt', 'Aldensart', 'Astermont', 'Astercourt',
    'Astersart', 'Brommont', 'Bromcourt', 'Bromsart', 'Caldenmont',
    'Caldencourt', 'Caldensart', 'Draymont', 'Draycourt', 'Draysart',
    'Elmmont', 'Elmcourt', 'Elmsart', 'Estermont', 'Estercourt', 'Estersart',
    'Fennmont', 'Fenncourt', 'Fennsart', 'Havenmont', 'Havencourt',
    'Havensart', 'Nylemont', 'Nylecourt', 'Nylesart', 'Rousmont',
    'Rouscourt', 'Steinmont', 'Steincourt', 'Steinsart', 'Vanemont',
    'Vanecourt', 'Vanesart', 'Ravmont', 'Ravcourt', 'Ravsart'],
  nordic: [
    'Aldendahl', 'Aldenholm', 'Asterdahl', 'Asterholm', 'Asterborg',
    'Bromdahl', 'Bromholm', 'Bromborg', 'Caldendahl', 'Caldenholm',
    'Caldenborg', 'Draydahl', 'Drayholm', 'Drayborg', 'Elmdahl', 'Elmholm',
    'Elmborg', 'Esterdahl', 'Esterholm', 'Esterborg', 'Fenndahl', 'Fennholm',
    'Fennborg', 'Havendahl', 'Havenholm', 'Havenborg', 'Nyledahl',
    'Nyleholm', 'Nyleborg', 'Rousdahl', 'Rousholm', 'Rousborg', 'Steindahl',
    'Steinholm', 'Steinborg', 'Vanedahl', 'Vaneholm', 'Vaneborg', 'Ravdahl',
    'Ravholm', 'Ravborg'],
  nl: [
    'Aldenven', 'Aldenrix', 'Asterven', 'Asterrix', 'Bromven', 'Bromrix',
    'Caldenven', 'Caldenrix', 'Drayven', 'Drayrix', 'Elmven', 'Elmrix',
    'Esterven', 'Esterrix', 'Fennven', 'Fennrix', 'Havenrix', 'Nyleven',
    'Nylerix', 'Rousven', 'Rousrix', 'Steinven', 'Steinrix', 'Vaneven',
    'Vanerix', 'Ravven', 'Ravrix'],
}

// A second word, used only when the real name had one before its suffix, so
// "ZF Friedrichshafen AG" stays three tokens long.
const DESCRIPTORS = {
  en: ['Group', 'Industries', 'Holdings', 'Engineering', 'Materials'],
  de: ['Werke', 'Technik', 'Industrie', 'Maschinenbau'],
  fr: ['Industrie', 'Matériaux', 'Technologies'],
  nordic: ['Industri', 'Verkstad', 'Teknik'],
  nl: ['Industrie', 'Techniek', 'Groep'],
}

// Legal suffix → region. Matched against the name with dots stripped, so
// "N.V.", "NV" and "N.V" all land in the same bucket; order matters, because
// "AB" (Nordic) would otherwise be shadowed by a looser rule.
const SUFFIX_REGIONS = [
  [/\b(gmbh|mbh|ag|se|kgaa|kg)$/i, 'de'],
  [/\b(sas|sarl|sca)$/i, 'fr'],
  [/\b(oyj|abp|ab|a\/s|asa|aps)$/i, 'nordic'],
  [/\b(nv|bv)$/i, 'nl'],
  [/\b(spa|srl)$/i, 'it'],
  [/\b(plc|ltd|limited)$/i, 'en'],
  [/\b(inc|incorporated|corp|corporation|llc|lp)$/i, 'us'],
  [/\b(sa)$/i, 'fr'],
  [/\b(holdings|group|co|company|international|intl)$/i, 'en'],
]

// Italy and the US have no stem pool of their own — Latin and Anglo stems
// carry those suffixes convincingly enough.
const STEM_POOL_FOR = { it: 'fr', us: 'en' }

// A real name with no legal suffix ("BMW", "ZF") would otherwise become a bare
// invented word, which reads as a codename rather than a company. It gets one
// from its region instead, so every name on screen ends like a company.
const DEFAULT_SUFFIXES = {
  en: ['plc', 'Ltd'],
  us: ['Inc', 'Corp'],
  de: ['GmbH', 'AG'],
  fr: ['SA', 'SAS'],
  nordic: ['AB', 'A/S'],
  nl: ['NV', 'BV'],
  it: ['SpA', 'Srl'],
}

// ── Short forms ────────────────────────────────────────────────────
//
// The company list carries legal entities; the reports call the same client
// something shorter. "Jaguar Land Rover" is registered, the brief says "JLR".
// A short form nobody registered is just an unregistered string, so the sweep
// walks straight past it — which is how the alert list ends up renaming the
// card header and leaving the real client in the title next to it.
//
// Most short forms are derivable from the registered name (see aliasesFor):
// a parenthetical, or a generic tail dropped. These are the rest — the ones
// no rule reaches, because the relationship is an initialism, a trade name or
// a word the registered name does not contain. Every entry below was taken
// from a scan of the brief titles actually in the estate, not guessed.
//
// Keyed on the registered name normalised (lower case, punctuation to spaces)
// AFTER its legal suffix comes off, so one key catches every spelling of it.
//
// The eventual home for this is `company_name_aliases` in the backend, which
// exists and is empty; until something populates it, it lives here.
//
// A short form that is also a place or a person is left out, even though it
// costs coverage: "Rio" for Rio Tinto turns Rio de Janeiro into a client, and
// "Miller" for Miller Insurance turns a surname into one. A visibly mangled
// city reads worse on a demo reel than a company name that stayed put.
const SHORT_FORMS = {
  'attire clothing': ['Attire'],
  'bucher industries': ['Bucher'],
  'cnh industrial': ['CNH'],
  'compagnie chargeurs invest': ['Chargeurs'],
  'electronic arts': ['EA'],
  'flight centre travel': ['Flight Centre'],
  'h m hennes mauritz': ['H&M'],
  'hitachi construction machinery': ['Hitachi'],
  'industria de diseno textil': ['Inditex', 'Zara'],
  'j c bamford excavators': ['JCB'],
  'jaguar land rover': ['JLR'],
  'jaguar land rover automotive': ['JLR'],
  'john deere': ['Deere'],
  'jupiter fund management': ['Jupiter'],
  'louis dreyfus': ['LDC'],
  'matalan retail': ['Matalan'],
  'mercedes benz': ['Mercedes'],
  'ming yang smart': ['Ming Yang'],
  'national education group': ['NEG'],
  'nervecentre software': ['Nervecentre'],
  'palo alto networks': ['Palo Alto'],
  'proctor and gamble': ['P&G'],
  'pz cussons': ['PZ'],
  'shell oil': ['Shell'],
  'sia engineering company': ['SIAEC'],
  'singapore airlines': ['SIA'],
  'soundhound ai': ['SoundHound'],
  'tanaka precious metals': ['Tanaka'],
  'tullow oil': ['Tullow'],
  'volkswagen': ['VW'],
  'volvo cars': ['Volvo'],
  'vulcan energy': ['Vulcan'],
  'wacker neuson': ['Wacker'],
  'zf friedrichshafen': ['ZF'],
}

// Tails a company drops when it talks about itself: "MOBICO GROUP PLC" loses
// PLC to the legal-suffix split above and is still called "Mobico". Dropped
// one at a time, and never down to something too short to be a name on its
// own — "IP Group" must not leave "IP" loose in the text.
const GENERIC_TAIL_RE = /[\s,]+(?:holdings?|group|groupe|international|intl)\.?$/i

const normKey = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

/**
 * Short forms of one registered name, all pointing at the same invented one.
 * `body` is the name with its legal suffix already off.
 *
 * `exact` means match the capitalisation too. A form cut out of the
 * registered name inherits its case-blindness — the row is "INTEGRAFIN
 * HOLDINGS PLC" and the prose is "IntegraFin". A form from SHORT_FORMS is
 * written the way the prose writes it, and several of them are ordinary
 * words: "Shell" is the client, "shell company" is not.
 */
function aliasesFor(body) {
  const out = []
  if (!body) return out

  // "Singapore Telecommunications Limited (Singtel)" is two names in one row.
  const paren = body.match(/\(([^)]{2,})\)/)
  if (paren) out.push({ text: paren[1].trim(), exact: false })
  const bare = body.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim()
  if (bare && bare !== body) out.push({ text: bare, exact: false })

  let head = bare || body
  for (let i = 0; i < 3; i++) {
    const next = head.replace(GENERIC_TAIL_RE, '').trim()
    if (next === head || next.replace(/[^\p{L}\p{N}]/gu, '').length < 3) break
    head = next
    out.push({ text: head, exact: false })
  }

  for (const text of SHORT_FORMS[normKey(body)] || []) out.push({ text, exact: true })
  return out
}

// Our own product is chrome, not a client. Without this the wordmark in the
// header is a text node like any other and the sweep renames the app.
const BRAND_RE = /^nviya\b/i

// People: common given name + common surname. Deliberately NOT invented —
// a coined surname reads as fake on a person. High-frequency names identify
// no individual, which is the property that actually matters.
const FAKE_PEOPLE = [
  'Martin Ellis', 'Claire Bennett', 'Stefan Brandt', 'Anna Roos',
  'Paul Hargreave', 'Ines Moreau', 'Thomas Lindqvist', 'Rachel Fenwick',
  'Andreas Keller', 'Sofia Marchetti', 'Helen Crowther', 'Marcus Feldmann',
  'Julia Renard', 'Nigel Ashworth', 'Karin Vogel', 'Peter Halloran',
  'Emma Whitfield', 'Johan Persson', 'Laura Vermeulen', 'Daniel Okafor',
  'Grace Adeyemi', 'Hannah Lindberg', 'Oliver Grant', 'Mei Lin Tan',
  'Raj Mehta', 'Carlos Ferrer', 'Yusuf Demir', 'Sarah Kowalski',
]

// Portfolio names often encode the client outright ("BMW Suppliers"), so they
// are replaced with flat descriptive labels rather than invented ones.
const FAKE_PORTFOLIOS = [
  'Industrials — Europe', 'Core Coverage', 'Manufacturing — Group A',
  'Diversified Industrials', 'Coverage Portfolio', 'Industrials — Group B',
  'Capital Goods', 'Core Industrials', 'Industrials — UK', 'Watchlist',
  'Manufacturing — Group B', 'Mid-Market Industrials', 'Automotive Supply',
  'Industrials — Nordics', 'Key Accounts', 'Transport & Logistics',
  'Materials Coverage', 'Industrials — DACH', 'Growth Coverage',
  'Heavy Equipment',
]

const TITLE_PREFIX_RE = /\b(Mr\.?|Mrs\.?|Ms\.?|Dr\.?|Prof\.?|CEO|CFO|CTO|COO|CMO|CIO|CSO|Chairman|Chairwoman|President|Director|Founder|Co-founder|Senator|Governor|Secretary|Minister|Analyst)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/g

const CORP_SUFFIX_RE = /[,.]?\s+(plc|p\.l\.c\.|ltd|limited|inc|incorporated|corp|corporation|co|company|group|holdings|s\.a\.s\.|sas|sarl|s\.a\.|sa|ag|gmbh|mbh|kgaa|kg|n\.v\.|nv|b\.v\.|bv|se|lp|llc|s\.p\.a\.|spa|s\.r\.l\.|srl|ab|abp|oyj|a\/s|asa|aps|international|intl)\.?$/i

// Letters that arrive accented in prose but plain in the company list, or the
// other way round: the row says "Nestle", every brief says "Nestlé".
const ACCENTS = {
  a: 'aàáâäãå', c: 'cç', e: 'eéèêë', i: 'iíìîï', n: 'nñ',
  o: 'oóòôöõø', s: 'sš', u: 'uúùûü', y: 'yýÿ', z: 'zž',
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * A pattern for a registered name that tolerates how the prose writes it:
 * accents ("Nestlé"), spacing ("ServiceNow", "H&M"), and the underscores a
 * couple of rows carry ("Wizz_Air_Holdings_Plc"). Word runs are matched in
 * order; everything between them is anything non-alphanumeric, or nothing.
 */
const fuzzyPattern = (real) =>
  real
    .trim()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .map((word) =>
      [...word]
        .map((ch) => ACCENTS[ch.toLowerCase()] ? `[${ACCENTS[ch.toLowerCase()]}]` : escapeRe(ch))
        .join(''))
    .join('[^\\p{L}\\p{N}]*')

// Unicode-aware edges. \b is ASCII-only, so it finds no boundary after the
// "é" in "Nestlé's" and the match fails at the one place it was needed.
const bounded = (pattern) => `(?<![\\p{L}\\p{N}])${pattern}(?![\\p{L}\\p{N}])`

/** FNV-1a. Small, fast, and stable across sessions — which is the whole point. */
function hashString(s) {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h >>> 0
}

/**
 * Pick from `pool` by hash, probing forward so no two reals share a fake.
 *
 * The numeric tail is a true last resort: "Nylestone 73" does not read as a
 * company, and the first cut hit it on every name because the pool was 23
 * stems against a corpus of ~90. Callers pass a pool large enough that the
 * probe finds a free entry — for companies, stems × descriptors.
 */
function pickStable(pool, key, taken) {
  const start = hashString(key) % pool.length
  for (let i = 0; i < pool.length; i++) {
    const candidate = pool[(start + i) % pool.length]
    if (!taken.has(candidate)) return candidate
  }
  // Pool exhausted — disambiguate rather than collide.
  return `${pool[start]} ${Math.floor(hashString(key) / pool.length) % 90 + 10}`
}

// ── Singleton state (module-level so all components share one map) ──
const isAnonymized = ref(false)
const companyMap = new Map()
// Short forms, kept apart from companyMap because they match under different
// rules: no fuzzing, and case-sensitively when they came from SHORT_FORMS.
const aliasMap = new Map()
const personMap = new Map()
const portfolioMap = new Map()
const takenCompanies = new Set()
const takenPeople = new Set()
const takenPortfolios = new Set()
let initialized = false

export function useAnonymize() {
  if (!initialized && import.meta.client) {
    isAnonymized.value = localStorage.getItem('nviya_anonymize') === 'true'
    initialized = true
  }

  /**
   * Split a company name into the stem we replace and the suffix we keep.
   * "ZF Friedrichshafen AG" → { body: 'ZF Friedrichshafen', suffix: 'AG' }
   */
  const splitName = (name) => {
    const m = name.match(CORP_SUFFIX_RE)
    if (!m) return { body: name.trim(), suffix: '' }
    return { body: name.slice(0, m.index).trim(), suffix: m[0].trim().replace(/^[,.]\s*/, '') }
  }

  const regionOf = (name) => {
    // Strip dots and trailing punctuation so "N.V." reads as "NV".
    const flat = name.trim().replace(/\./g, '').replace(/[,\s]+$/, '')
    for (const [re, region] of SUFFIX_REGIONS) if (re.test(flat)) return region
    return 'en'
  }

  /**
   * Map a real company name to a stable invented one that keeps its shape.
   * Registers the mapping so free-text replacement can find it later.
   */
  const anonCompany = (name) => {
    if (!isAnonymized.value || !name) return name
    if (BRAND_RE.test(name.trim())) return name
    if (companyMap.has(name)) return companyMap.get(name)

    // Underscores stand in for spaces in a few rows ("Wizz_Air_Holdings_Plc").
    // Split on the readable form so the suffix comes off and the short forms
    // are derivable; the map still keys on the name exactly as it arrived.
    const flat = name.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim()
    const { body, suffix } = splitName(flat)
    const region = regionOf(flat)
    const pool = STEMS[STEM_POOL_FOR[region] || region] || STEMS.en
    const descriptors = DESCRIPTORS[STEM_POOL_FOR[region] || region] || DESCRIPTORS.en

    // One-word and two-word forms of the same pool. A corpus of ~90 clients
    // overran a flat list of stems and every name came out numbered, so the
    // probe runs over the cross-product instead: shape-matching candidates
    // first, then the other shape rather than a number.
    const singles = pool
    const pairs = descriptors.flatMap((d) => pool.map((stem) => `${stem} ${d}`))
    const wordsBeforeSuffix = body.split(/\s+/).filter(Boolean).length
    // "Deere & Company" keeps "Company" as its suffix, so adding a descriptor
    // on top gives "Havenmere Group Company". Where the real suffix is already
    // an English word, the stem stands alone.
    const suffixIsWord = /^(co|company|group|holdings|international|intl)$/i
      .test(suffix.replace(/\.$/, ''))
    const wantsTwoWords = wordsBeforeSuffix > 1 && !suffixIsWord &&
      !/^[A-Z&.\s]{2,5}$/.test(body)
    const candidates = wantsTwoWords ? [...pairs, ...singles] : [...singles, ...pairs]

    const shortFake = pickStable(candidates, body.toLowerCase(), takenCompanies)
    takenCompanies.add(shortFake)

    // Keep the real suffix where there is one; where there is none, give it
    // the region's own, so an invented name still reads as a legal entity.
    const defaults = DEFAULT_SUFFIXES[region] || DEFAULT_SUFFIXES.en
    const tail = suffix || defaults[hashString(body) % defaults.length]
    const fake = `${shortFake} ${tail}`

    companyMap.set(name, fake)
    // A registered name is never also an alias: this client owns the string.
    for (const k of [...aliasMap.keys()]) {
      if (k.toLowerCase() === name.toLowerCase()) aliasMap.delete(k)
    }
    // The short form maps to the short fake, not the full one. Prose that says
    // "CRH declined" must read "Nylemere declined", not "Nylemere plc declined".
    if (body && body !== name && !companyMap.has(body)) companyMap.set(body, shortFake)
    for (const { text, exact } of aliasesFor(body)) registerAlias(text, shortFake, exact)
    return fake
  }

  /**
   * Point a short form at a company's invented name. Never overwrites: the
   * first claim wins, and a string that is some other client's registered
   * name is not an alias at all — "SAS" is both an airline in the list and
   * the short form of another row, and the list gets it.
   */
  const registerAlias = (alias, fake, exact) => {
    const a = (alias || '').trim()
    if (a.length < 2 || aliasMap.has(a) || companyMap.has(a)) return
    const lower = a.toLowerCase()
    for (const k of companyMap.keys()) if (k.toLowerCase() === lower) return
    aliasMap.set(a, { fake, exact })
  }

  /** Portfolio names get a flat label — they often name the client outright. */
  const anonPortfolio = (name) => {
    if (!isAnonymized.value || !name) return name
    if (BRAND_RE.test(name.trim())) return name
    if (!portfolioMap.has(name)) {
      const fake = pickStable(FAKE_PORTFOLIOS, name.toLowerCase(), takenPortfolios)
      takenPortfolios.add(fake)
      portfolioMap.set(name, fake)
    }
    return portfolioMap.get(name)
  }

  const anonPerson = (name) => {
    if (!isAnonymized.value || !name) return name
    // Already an invented name — the DOM sweep passes over the same text more
    // than once, and without this each pass would rename the person again.
    if (takenPeople.has(name)) return name
    if (!personMap.has(name)) {
      const fake = pickStable(FAKE_PEOPLE, name.toLowerCase(), takenPeople)
      takenPeople.add(fake)
      personMap.set(name, fake)
    }
    return personMap.get(name)
  }

  /**
   * Every registered real name, longest first, with a word-boundary-safe
   * regex. Longest first so "CRH plc" is consumed before "CRH".
   */
  const replacements = () => {
    const named = [...companyMap.entries(), ...portfolioMap.entries()]
      .sort((a, b) => b[0].length - a[0].length)
      .map(([real, fake]) => [new RegExp(bounded(fuzzyPattern(real)), 'giu'), fake])

    // Short forms run last, whatever their length: "SIA Engineering Company"
    // has to be spent before the "SIA" that starts it can claim the line.
    const short = [...aliasMap.entries()]
      .sort((a, b) => b[0].length - a[0].length)
      .map(([real, { fake, exact }]) =>
        [new RegExp(bounded(escapeRe(real)), exact ? 'gu' : 'giu'), fake])

    return [...named, ...short]
  }

  /**
   * Replace person names that follow a title ("CEO Jane Doe"). Split out from
   * anonymize() because the DOM sweep applies it per text node, and must not
   * rebuild the company regex list for each one.
   */
  const personPass = (text) => {
    if (!isAnonymized.value || !text) return text
    return text.replace(TITLE_PREFIX_RE, (match, title, name) =>
      `${title} ${anonPerson(name.trim())}`)
  }

  /** Replace registered names, plus person names that follow a title. */
  const anonymize = (text) => {
    if (!isAnonymized.value || !text) return text
    let result = text
    for (const [re, fake] of replacements()) result = result.replace(re, fake)
    return personPass(result)
  }

  /** Pre-register names so free text can be swept before they first render. */
  const registerCompanies = (names) => {
    if (!isAnonymized.value) return
    for (const name of names || []) if (name) anonCompany(name)
  }

  const registerPortfolios = (names) => {
    if (!isAnonymized.value) return
    for (const name of names || []) if (name) anonPortfolio(name)
  }

  const clearMaps = () => {
    companyMap.clear(); aliasMap.clear(); personMap.clear(); portfolioMap.clear()
    takenCompanies.clear(); takenPeople.clear(); takenPortfolios.clear()
  }

  /** Toggle demo mode on/off */
  const toggle = () => {
    isAnonymized.value = !isAnonymized.value
    if (import.meta.client) {
      localStorage.setItem('nviya_anonymize', String(isAnonymized.value))
    }
    if (!isAnonymized.value) clearMaps()
  }

  return {
    isAnonymized: readonly(isAnonymized),
    toggle,
    anonCompany,
    anonPerson,
    anonPortfolio,
    anonymize,
    personPass,
    registerCompanies,
    registerPortfolios,
    replacements,
  }
}
